export interface FilterOption {
  label: string;
  groupLabel: string;
  value: string;
}

/**
 * Ensures that each filter group has a "Not Specified" option.
 * This function automatically adds a "Not Specified" filter for each groupLabel
 * if one doesn't already exist, so callers don't need to manually track and add them.
 *
 * @param filters - Array of filter options to process
 * @returns Array of filter options with "Not Specified" added for each group that needs it
 */
export function appendNotSpecifiedFilterOption(filters: FilterOption[]): FilterOption[] {
  if (!filters || filters.length === 0) {
    return filters;
  }

  // Group filters by groupLabel
  const groupedFilters = new Map<string, FilterOption[]>();
  filters.forEach(filter => {
    const existing = groupedFilters.get(filter.groupLabel) ?? [];
    existing.push(filter);
    groupedFilters.set(filter.groupLabel, existing);
  });

  const result: FilterOption[] = [];

  // For each group, add all filters and ensure "Not Specified" exists
  groupedFilters.forEach((groupFilters, groupLabel) => {
    // Add all existing filters for this group
    result.push(...groupFilters);

    // Check if this group already has a "Not Specified" filter
    const hasNotSpecified = groupFilters.some(
      filter => filter.label === "Not Specified" || filter.value.endsWith(":undefined")
    );

    if (!hasNotSpecified) {
      // Extract the filter type from the first filter's value (e.g., "category" from "category:value")
      const firstFilter = groupFilters[0];
      const filterType = firstFilter?.value.split(":")[0];

      if (filterType) {
        result.push({
          label: "Not Specified",
          groupLabel,
          value: `${filterType}:undefined`,
        });
      }
    }
  });

  return result;
}
